import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../lib/utils";

// shadcn-style Tooltip on the Radix primitive.
// See: https://ui.shadcn.com/docs/components/tooltip

/**
 * Shares the open delay between the tooltips under it: once one tooltip has shown,
 * moving to the next trigger shows that one at once (`skipDelayDuration`). Wrap a
 * group of icon buttons (a sidebar rail) in one provider to get that behaviour.
 */
function TooltipProvider({
  delayDuration = 100,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />;
}

/**
 * One tooltip. It brings its own provider, so it works anywhere without an app-level
 * `TooltipProvider`; a Radix tooltip without one throws at render. The price is that
 * a provider above it does not reach it, so for a group that should share the delay
 * use `TooltipProvider` + `TooltipRoot` instead.
 */
function Tooltip({
  delayDuration,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

/** The bare Radix root, for use under a shared `TooltipProvider`. */
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    /** Draw the small pointer towards the trigger. On by default. */
    arrow?: boolean;
  }
>(({ className, sideOffset = 6, arrow = true, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      className={cn(
        // Above every other layer: a tooltip only lives while the pointer rests on its
        // trigger, and that trigger can sit inside a dialog (z-[70]), a popover
        // (z-[120]) or a Select list (z-[210]). Radix copies this value onto its popper
        // wrapper as an inline style, so it has to be right here.
        "z-[230] max-w-xs rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background shadow-md",
        "animate-in fade-in-0 zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    >
      {children}
      {arrow && <TooltipPrimitive.Arrow className="fill-foreground" width={10} height={5} />}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipRoot, TooltipTrigger, TooltipContent, TooltipProvider };
